import { useCallback, useRef, useState } from "react";
import { apiUpload } from "../../lib/api-client";
import AbbreviationTooltip from "../ui/AbbreviationTooltip";

export interface DWREvent {
  event_type: string;
  description: string;
  depth_top_ft?: number;
  depth_bottom_ft?: number;
}

export interface DWRDailySummary {
  day_number: number;
  work_date: string;
  narrative: string;
  events: DWREvent[];
}

export interface DWRActualPlug {
  plug_number: number;
  type: string;
  top_ft: number;
  bottom_ft: number;
  sacks: number;
  cement_class: string;
}

export interface DWRPreviewData {
  api_number: string;
  total_days: number;
  start_date: string;
  end_date: string;
  daily_summaries: DWRDailySummary[];
  actual_plugs: DWRActualPlug[];
  confidence: number;
  warnings: string[];
}

interface DWRUploaderProps {
  formId: number;
  onUploadComplete: (data: DWRPreviewData) => void;
  onError: (error: string) => void;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const colorClass =
    pct >= 80
      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
      : pct >= 60
      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      <span>Confidence: {pct}%</span>
    </span>
  );
}

export default function DWRUploader({ formId, onUploadComplete, onError }: DWRUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<DWRPreviewData | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const pdfs = Array.from(incoming).filter((f) => f.type === "application/pdf");
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...pdfs.filter((f) => !existing.has(f.name))];
    });
  };

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(10);

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));

      // Simulate progress increments while waiting
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 10, 85));
      }, 1500);

      const data = await apiUpload<DWRPreviewData>(
        `/api/c103/forms/${formId}/upload-dwr/`,
        formData
      );

      clearInterval(progressInterval);
      setProgress(100);
      setPreview(data);
      onUploadComplete(data);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          isDragOver
            ? "border-brand-500 bg-brand-50 dark:bg-brand-900/10"
            : "border-gray-300 hover:border-brand-400 dark:border-white/[0.15] dark:hover:border-brand-500/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="flex flex-col items-center gap-2">
          <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Drop <AbbreviationTooltip term="DWR" /> PDFs here or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Only PDF files accepted. Multiple files allowed.
          </p>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-white/[0.05] divide-y divide-gray-100 dark:divide-white/[0.05]">
          {files.map((f) => (
            <div key={f.name} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{f.name}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {(f.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                disabled={uploading}
                className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40 ml-3"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {uploading && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Parsing DWR data...</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-brand-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload button */}
      {!preview && (
        <button
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
          className="w-full py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? "Uploading & Parsing..." : `Upload ${files.length > 0 ? `${files.length} File${files.length !== 1 ? "s" : ""}` : "Files"}`}
        </button>
      )}

      {/* Preview */}
      {preview && (
        <div className="rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/10 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-green-800 dark:text-green-300">
              DWR Parsed Successfully
            </p>
            <ConfidenceBadge confidence={preview.confidence} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
            {[
              { label: "API Number", value: preview.api_number },
              { label: "Total Days", value: String(preview.total_days) },
              { label: "Start Date", value: preview.start_date },
              { label: "Plugs Found", value: String(preview.actual_plugs.length) },
            ].map((item) => (
              <div key={item.label} className="rounded-lg bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] p-2.5">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">{item.label}</p>
                <p className="mt-0.5 font-semibold text-gray-800 dark:text-white/90">{item.value}</p>
              </div>
            ))}
          </div>

          {preview.warnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase">
                Warnings ({preview.warnings.length})
              </p>
              {preview.warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-700 dark:text-amber-300">
                  - {w}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
